<template>
  <div>
    <div v-if="pageLoading" class="text-center p-4">
      Loading change requests.....
    </div>

    <div v-else>
      <div class="card">
        <div class="card-header">
          <div class="d-flex align-items-center justify-content-between">
            <h4 class="card-title">Change Requests</h4>
            <div>
              <button
                v-if="permissions.includes('create-change-request')"
                class="btn btn-primary btn-sm"
                @click.prevent="openCreateModal"
              >
                Create Change Request
              </button>
            </div>
          </div>
        </div>
        <div class="card-content">
          <div class="card-body">
            <CDataTable
              :items="requests"
              :fields="fields"
              pagination
              sorter
              tableFilter
            >
              <template #reference="{item}">
                <td>{{ item.reference }}</td>
              </template>
              <template #category="{item}">
                <td>{{ item.category }}</td>
              </template>
              <template #description="{item}">
                <td>{{ item.description }}</td>
              </template>
              <template #logged_user="{item}">
                <td>
                  {{
                    item.logged_user
                      ? item.logged_user.first_name +
                        " " +
                        item.logged_user.last_name
                      : "-"
                  }}
                </td>
              </template>
              <template #approver="{item}">
                <td>
                  {{
                    item.approver
                      ? item.approver.first_name + " " + item.approver.last_name
                      : "-"
                  }}
                </td>
              </template>
              <template #status="{item}">
                <td class="text-capitalize">{{ item.status }}</td>
              </template>
              <template #actions="{item}">
                <td>
                  <button
                    class="btn btn-sm btn-secondary me-2"
                    @click.prevent="openDetail(item)"
                  >
                    View
                  </button>
                </td>
              </template>
            </CDataTable>

            <CPagination
              :activePage.sync="next_page"
              :pages="total"
              :onActivePageChange="onPageChange"
            />
          </div>
        </div>
      </div>
    </div>

    <modal
      v-show="showDetailModal"
      @close="closeDetailModal"
      modalIsLarge
      footer
    >
      <template v-slot:title>
        <h5>Change Request Details</h5>
      </template>
      <template v-slot:body>
        <div v-if="selected">
          <p><strong>Reference:</strong> {{ selected.reference }}</p>
          <p><strong>Category:</strong> {{ selected.category }}</p>
          <p><strong>Description:</strong> {{ selected.description }}</p>
          <p><strong>Status:</strong> {{ selected.status }}</p>
          <div class="row">
            <div class="col-md-6">
              <h6>Old Value</h6>
              <pre>{{ prettyOld }}</pre>
            </div>
            <div class="col-md-6">
              <h6>New Value</h6>
              <pre>{{ prettyNew }}</pre>
            </div>
          </div>
        </div>
      </template>
      <template v-slot:footer>
        <div v-if="selected && selected.status === 'pending' && permissions.includes('treat-change-request')">
          <button
            class="btn btn-danger me-2"
            :disabled="treatLoading"
            @click.prevent="confirmTreat('rejected')"
          >
            <span v-if="treatLoading">Please wait...</span>
            <span v-else>Reject</span>
          </button>
          <button
            class="btn btn-success me-2"
            :disabled="treatLoading"
            @click.prevent="confirmTreat('approved')"
          >
            <span v-if="treatLoading">Please wait...</span>
            <span v-else>Approve</span>
          </button>
        </div>
        <button class="btn btn-secondary" @click="closeDetailModal">
          Close
        </button>
      </template>
    </modal>

    <modal
      v-show="showCreateModal"
      @close="closeCreateModal"
      modalIsLarge
      footer
    >
      <template v-slot:title>
        <h5>Create Change Request</h5>
      </template>
      <template v-slot:body>
        <div class="row">
          <div class="col-md-12 mb-2">
            <label>Select fields to change</label>
            <div class="form-check">
              <input
                class="form-check-input"
                type="checkbox"
                v-model="createFields.first_name"
                id="ch_first_name"
              />
              <label class="form-check-label" for="ch_first_name"
                >First name</label
              >
            </div>
            <div class="form-check">
              <input
                class="form-check-input"
                type="checkbox"
                v-model="createFields.last_name"
                id="ch_last_name"
              />
              <label class="form-check-label" for="ch_last_name"
                >Last name</label
              >
            </div>
            <div class="form-check">
              <input
                class="form-check-input"
                type="checkbox"
                v-model="createFields.phone_number"
                id="ch_phone_number"
              />
              <label class="form-check-label" for="ch_phone_number"
                >Phone number</label
              >
            </div>
          </div>

          <div class="col-md-6" v-if="createFields.first_name">
            <div class="form-group">
              <label>First name</label>
              <input
                type="text"
                class="form-control"
                v-model="createPayload.first_name"
              />
            </div>
          </div>
          <div class="col-md-6" v-if="createFields.last_name">
            <div class="form-group">
              <label>Last name</label>
              <input
                type="text"
                class="form-control"
                v-model="createPayload.last_name"
              />
            </div>
          </div>
          <div class="col-md-6" v-if="createFields.phone_number">
            <div class="form-group">
              <label>Phone number</label>
              <input
                type="text"
                class="form-control"
                v-model="createPayload.phone_number"
              />
            </div>
          </div>
          <div
            class="col-md-12"
            v-if="
              createFields.first_name ||
                createFields.last_name ||
                createFields.phone_number
            "
          >
            <div class="form-group">
              <label>Description</label>
              <textarea
                class="form-control"
                v-model="createPayload.description"
                placeholder="description"
              ></textarea>
            </div>
          </div>
        </div>
      </template>
      <template v-slot:footer>
        <button
          class="btn btn-primary me-2"
          :disabled="createLoading"
          @click.prevent="confirmCreate"
        >
          <span v-if="createLoading">Please wait...</span>
          <span v-else>Create</span>
        </button>
        <button class="btn btn-secondary" @click.prevent="closeCreateModal">
          Cancel
        </button>
      </template>
    </modal>
  </div>
</template>

<script>
import ApiService from "@/services/api.service";
import ErrorService from "@/services/error.service";
import Swal from "sweetalert2";
import CDataTable from "@/components/CDataTable";
import CPagination from "@/components/CPagination";

export default {
  name: "ChangeRequestComponent",
  props: {
    customer_user_id: {
      type: [String, Number],
      required: true
    },
    onRefresh: {
      type: Function,
      required: false
    }
  },
  components: { CDataTable, CPagination },
  data() {
    return {
      requests: [],
      pageLoading: true,
      next_page: 1,
      current: 1,
      total: 1,
      selected: null,
      showDetailModal: false,
      treatLoading: false,
      showCreateModal: false,
      createLoading: false,
      createFields: {
        first_name: false,
        last_name: false,
        phone_number: false
      },
      createPayload: {
        category: "",
        first_name: "",
        last_name: "",
        phone_number: "",
        description: ""
      },
      fields: [
        "reference",
        "category",
        "description",
        "logged_user",
        "approver",
        "status",
        "actions"
      ]
    };
  },
  computed: {
    prettyOld() {
      if (!this.selected || !this.selected.old_value) return "";
      try {
        return JSON.stringify(JSON.parse(this.selected.old_value), null, 2);
      } catch (e) {
        return this.selected.old_value;
      }
    },
    prettyNew() {
      if (!this.selected || !this.selected.new_value) return "";
      try {
        return JSON.stringify(JSON.parse(this.selected.new_value), null, 2);
      } catch (e) {
        return this.selected.new_value;
      }
    },
    permissions() {
      let permissions = this.$store.getters.permissions;
      return permissions;
    }
  },
  methods: {
    async getChangeRequests(page = 1) {
      this.pageLoading = true;
      try {
        const response = await ApiService.get(
          `change_request/list?page=${page}&customer_user_id=${this.customer_user_id}`
        );
        const data = response.data.data || {};
        console.log("Change Requests Data:", data);
        this.requests = data.data ? data.data : [];
        this.current = data.current_page || 1;
        this.total = data.last_page || 1;
        this.pageLoading = false;
      } catch (error) {
        this.pageLoading = false;
        console.error(error);
        if (error && error.response && error.response.data) {
          ErrorService.displayErrorAlert(error.response.data);
        }
      }
    },
    onPageChange() {
      if (this.current !== this.next_page) {
        this.getChangeRequests(this.next_page);
      }
    },
    openDetail(item) {
      this.selected = item;
      this.showDetailModal = true;
    },
    closeDetailModal() {
      // this.selected = null;
      this.showDetailModal = false;
    },
    async confirmTreat(status) {
      if (!this.selected) return;
      this.closeDetailModal();
      const resp = await Swal.fire({
        title: "Are you sure?",
        html: `This will mark request <strong>${this.selected.reference}</strong> as <strong class="text-capitalize">${status}</strong>.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, continue!",
        cancelButtonText: "Cancel"
      });

      if (!resp.isConfirmed) return;
      this.showDetailModal = false;
      this.treatChangeRequest(status);
    },
    async treatChangeRequest(status) {
      if (!this.selected) return;
      this.treatLoading = true;
      try {
        const payload = { reference: this.selected.reference, status };
        const res = await ApiService.post("change_request/treat", payload);
        this.treatLoading = false;
        this.showDetailModal = false;
        Swal.fire({
          icon: "success",
          title: "Success",
          text: res.data.message || "Request updated"
        });
        // refresh list
        this.getChangeRequests(this.current || 1);
        if (this.onRefresh) this.onRefresh();
      } catch (err) {
        this.treatLoading = false;
        if (err && err.response && err.response.data) {
          ErrorService.displayErrorAlert(err.response.data);
        } else {
          console.error(err);
        }
      }
    },
    openCreateModal() {
      this.showCreateModal = true;
      // reset
      this.createFields = {
        first_name: false,
        last_name: false,
        phone_number: false
      };
      this.createPayload = {
        category: "",
        first_name: "",
        last_name: "",
        phone_number: "",
        description: ""
      };
    },
    closeCreateModal() {
      this.showCreateModal = false;
    },
    async confirmCreate() {
      // basic validation

      
      if (
        !this.createPayload.first_name &&
        !this.createPayload.last_name &&
        !this.createPayload.phone_number
      ) {
        return Swal.fire({
          icon: "error",
          title: "At least one field is required"
        });
      }

      if (!this.createPayload.description) {
        return Swal.fire({ icon: "error", title: "Description is required" });
      }

      // close modal and create
      this.showCreateModal = false;

      const resp = await Swal.fire({
        title: "Confirm create",
        html: `Create change request for <strong>${this.customer_user_id}</strong>?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, create",
        cancelButtonText: "Cancel"
      });
      if (!resp.isConfirmed) return;
      this.closeCreateModal();
      this.createChangeRequest();
    },
    async createChangeRequest() {
      this.createLoading = true;
      try {
        const payload = {
          customer_user_id: this.customer_user_id,
          category: "Bio Data Change",
          description: this.createPayload.description
        };
        if (this.createFields.first_name)
          payload.first_name = this.createPayload.first_name;
        if (this.createFields.last_name)
          payload.last_name = this.createPayload.last_name;
        if (this.createFields.phone_number)
          payload.phone_number = this.createPayload.phone_number;

        const res = await ApiService.post("change_request/create", payload);
        this.createLoading = false;
        this.showCreateModal = false;
        Swal.fire({
          icon: "success",
          title: "Created",
          text: res.data.message || "Change request created"
        });
        // refresh list
        this.getChangeRequests(1);
        if (this.onRefresh) this.onRefresh();
      } catch (err) {
        this.createLoading = false;
        if (err && err.response && err.response.data) {
          ErrorService.displayErrorAlert(err.response.data);
        } else {
          console.error(err);
        }
      }
    },
    async treatChangeRequest(status) {
      if (!this.selected) return;
      this.treatLoading = true;
      try {
        const payload = { reference: this.selected.reference, status };
        const res = await ApiService.post("change_request/treat", payload);
        this.treatLoading = false;
        this.showDetailModal = false;
        Swal.fire({
          icon: "success",
          title: "Success",
          text: res.data.message || "Request updated"
        });
        // refresh list
        this.getChangeRequests(this.current || 1);
        if (this.onRefresh) this.onRefresh();
      } catch (err) {
        this.treatLoading = false;
        if (err && err.response && err.response.data) {
          ErrorService.displayErrorAlert(err.response.data);
        } else {
          console.error(err);
        }
      }
    }
  },
  watch: {
    customer_user_id: {
      immediate: true,
      handler(val) {
        if (val && this.can("change-request")) this.getChangeRequests(1);
      }
    }
  },
  mounted() {
    if (this.customer_user_id && this.can("change-request")) this.getChangeRequests(1);
  }
};
</script>

<style scoped>
pre {
  background: #f8f9fa;
  padding: 10px;
  border-radius: 4px;
  font-size: 13px;
}
</style>
